import { MenuIcon } from "lucide-react";
import { createContext, useContext, type ReactNode } from "react";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { cn } from "@/lib/utils.ts";

type ShellValue = { openNavigation: () => void };

export const ShellContext = createContext<ShellValue>({ openNavigation: () => {} });

export function useShell(): ShellValue {
  return useContext(ShellContext);
}

/** Opens the sidebar drawer; hidden where the sidebar is always visible (≥1280px). */
export function NavigationButton({ className }: { className?: string }) {
  const { t } = useI18n();
  const { openNavigation } = useShell();
  return (
    <Button variant="ghost-muted" size="icon-sm" className={cn("xl:hidden", className)} aria-label={t.shell.openMenu} onClick={openNavigation}>
      <MenuIcon />
    </Button>
  );
}

/** The bar at the top of settings, usage, and admin pages. */
export function TopBar({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b bg-background/90 px-3 backdrop-blur sm:px-5 xl:hidden">
      <NavigationButton />
      {title ? <span className="min-w-0 truncate text-[14px] font-semibold">{title}</span> : null}
      {children}
      {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
    </header>
  );
}
